/**
 * ----------------------------------------------------------------------------
 * Nonconformity Component
 * ----------------------------------------------------------------------------
 * Summary:
 * Component for displaying and managing nonconformity records in a paginated
 * table. Supports default, approved, and verified views, expandable item
 * details, date formatting, refresh functionality, pagination, page-size
 * selection, and role-based approve and verify actions.
 *
 * @author Chandan Singh
 * @created August-2026
 * ----------------------------------------------------------------------------
 */

import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  OrderTableDefaultViewIcon,
  OrderTableShowAssignedIcon,
  OrderTableShowCompletedIcon,
  RefreshIcon,
  VerifiedIcon, ApprovedIcon
} from '../../shared-components/svgs/svgs';
import Header from '../../shared-components/header/header';
import Pagination from '../../shared-components/Grid/Pagination';
import IPageInfo, {
  IActionFormatter,
  IPageLimitInfo,
} from '../../models/ICommon';
import { useStore } from '../../contexts/store-provider';
import { FormattedMessage } from 'react-intl';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import { formatMessage } from '../../translations/format-message';
import { ModuleType } from '../../constants/enums/module-type-enum';
import { showIconsRoleBase } from '../../helpers/action-permission-helper';
import authStore from '../../core/stores/auth-store';
import userTypeEnum from '../../constants/enums/user-type-enum';
import './nonconformity.css';
import AddUpdateNonConformity from '../../shared-components/add-update-non-conformity/add-update-non-conformity';
import NonConformityPopupHOC from '../operator/hoc/non-conformity-popup-hoc';
import { IGetNonConformance } from '../../models/response/IAddedNonConformanceResponse';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import NonConformityStatusEnum from '../../constants/enums/non-conformance-status-enum';
import noMessageIcon from '../../assets/Images/no-noconfirmitry.png';
import Grid from '../../shared-components/Grid/Grid';

import { GridRemoteConstant } from '../../constants/grid-constant';
import IDefaultColumSortOrder from '../../models/IDefaultColumSortOrder';
import { emptyValue } from '../../constants/constants';
import { format } from 'date-fns';
import { DateFormatNew } from '../../constants/date-picker-constant';

type ViewMode = 'normal' | 'assigned' | 'completed';

const NC_STATUS = {
  DEFAULT_VIEW: -1,
  WAITING_FOR_APPROVAL: 0,
  WAITING_FOR_VERIFICATION: 2,
} as const;

const formatDate = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return format(d, DateFormatNew);
};

const getStatusForViewMode = (mode: ViewMode): number => {
  switch (mode) {
    case 'assigned':
      return NC_STATUS.WAITING_FOR_APPROVAL;
    case 'completed':
      return NC_STATUS.WAITING_FOR_VERIFICATION;
    default:
      return NC_STATUS.DEFAULT_VIEW;
  }
};

const Nonconformity = (props: any) => {
  const { preferencesStore, nonconformityStore } = useStore();
  const { userSetting } = preferencesStore;

  const {
    NonConformanceResponseList,
    allNonConformance,
    getAllNonConformanceState,
    GetAllNonConformanceService,
    GetNonConformanceByIdService,
    getNonConformanceByIdState,
    nonConformity,
    resetGetNonConformanceByIdState,
    approveNonConformityState,
    resetApproveNonConformityState,
    verifyNonConformityState,
    resetVerifyNonConformanceState,
  } = nonconformityStore;

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });
  const pageLimitOptions: Array<number> = [10, 20, 50, 100];
  const [disablePagination, setDisablePagination] = useState<boolean>(false);

  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('normal');
  const [columSortOrder, setColumnSortOrder] = useState<IDefaultColumSortOrder>({
    dataField: 'DetectedOn',
    order: 'desc',
  });
  const [isTableScrolled, setIsTableScrolled] = useState(false);
  const [selectedRow, setSelectedRow] = useState<
    IGetNonConformance | undefined
  >(undefined);
  const scrollSentinelRef = useRef<HTMLDivElement>(null);

  const getNonConformanceListServiceHandler = () => {
    GetAllNonConformanceService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      columSortOrder.dataField,
      columSortOrder.order,
      getStatusForViewMode(viewMode)
    );
  };

  const handleTableChange = (type: any, newState: any) => {
    if (type === 'sort' && newState.sortField) {
      if (
        newState.sortField !== columSortOrder.dataField ||
        newState.sortOrder !== columSortOrder.order
      ) {
        setColumnSortOrder({
          dataField: newState.sortField,
          order: newState.sortOrder,
        });
        GetAllNonConformanceService(
          pageInfo.currentPage,
          pageLimitInfo.pageLimit,
          newState.sortField,
          newState.sortOrder,
          getStatusForViewMode(viewMode)
        );
      }
    }
  };

  // Toggle a faint header shadow once the table has been scrolled past its
  // top edge, regardless of whether the page or an ancestor container owns
  // the actual scrolling.
  useEffect(() => {
    const sentinel = scrollSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsTableScrolled(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    getNonConformanceListServiceHandler();
  }, [pageInfo.currentPage, pageLimitInfo.pageLimit, viewMode]);

  // Keep local pageInfo.totalPages in sync with what the store actually returned
  useEffect(() => {
    setPageInfo((prev) => ({
      ...prev,
      totalPages: NonConformanceResponseList.PagingDetails.TotalPages,
    }));
  }, [NonConformanceResponseList.PagingDetails.TotalPages]);

  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(evt.target.value, 10);
    setPageLimit({
      pageLimit: newLimit,
      isPageLimitChange: true,
    });
    setPageInfo((prev) => ({ ...prev, currentPage: 1 }));
  };

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const toggleItems = (id: number) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleRowKeyDown = (
    evt: React.KeyboardEvent<HTMLTableRowElement>,
    id: number
  ) => {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      toggleItems(id);
    }
  };

  const handleViewModeChange = (mode: ViewMode) => {
    if (viewMode === mode && pageInfo.currentPage === 1) {
      getNonConformanceListServiceHandler();
    } else {
      setViewMode(mode);
      setPageInfo((prev) => ({ ...prev, currentPage: 1 }));
    }
  };

  const handleRefresh = () => {
    getNonConformanceListServiceHandler();
  };

  useEffect(() => {
    if (getNonConformanceByIdState.success) {
      approveHandler();
      resetGetNonConformanceByIdState();
    }
  }, [getNonConformanceByIdState.success]);

  useEffect(() => {
    if (approveNonConformityState.success) {
      resetApproveNonConformityState();
      getNonConformanceListServiceHandler();
    }
  }, [approveNonConformityState.success]);

  useEffect(() => {
    if (approveNonConformityState.error) {
      // toast.error(formatMessage(approveNonConformityState.error));
      resetApproveNonConformityState();
    }
  }, [approveNonConformityState.error]);

  useEffect(() => {
    if (verifyNonConformityState.success) {
      resetVerifyNonConformanceState();
      getNonConformanceListServiceHandler();
    }
  }, [verifyNonConformityState.success]);

  useEffect(() => {
    if (verifyNonConformityState.error) {
      // toast.error(formatMessage(verifyNonConformityState.error));
      resetVerifyNonConformanceState();
    }
  }, [verifyNonConformityState.error]);

  const approveClickHandler = (record: IGetNonConformance) => {
    GetNonConformanceByIdService(
      Number(getTenantIdFromLocalStorage()),
      record?.Id
    );
    // call approve API / open confirm modal, then refresh grid
  };

  const approveHandler = () => {
    setSelectedRow(nonConformity);
    if (
      nonConformity.Status === NonConformityStatusEnum.Pending ||
      nonConformity.Status === NonConformityStatusEnum.Verify
    ) {
      props.nonConformityToggleHandler();
    }
  };

  const isOperator =
    authStore.getUserType === userTypeEnum[userTypeEnum.Operator];

  const actionIcons: IActionFormatter[] = showIconsRoleBase(
    [
      {
        dataField: 'isApproved',
        handler: approveClickHandler,
        icon: <ApprovedIcon />,
        isDisable: isOperator,
        title: 'approve',
        varient: 'success',
        action: 'approve',
      },
      {
        dataField: 'isVerified',
        handler: approveClickHandler,
        icon: <VerifiedIcon />,
        isDisable: isOperator,
        title: 'verify',
        varient: 'primary',
        action: 'verify',
      },
    ],
    ModuleType.NonConformity
  );

  const columns: any = [

    {
      dataField: 'TrayId',
      text: formatMessage('tray_id'),
      classes: 'show-Elipses',
      formatter: (cell) => (
        <span className="nc-pill">{cell}</span>
      ),
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'DetectedOn',
      text: formatMessage('detection_date'),
      classes: 'show-Elipses',
      formatter: (cell) => (
        <span className="nc-pill">{formatDate(cell)}</span>
      ),
      sort: true,
    },

    {
      dataField: 'DetectionProcessName',
      text: formatMessage('detection_process'),
      classes: 'show-Elipses',
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'DetectedByName',
      text: formatMessage('detector'),
      classes: 'show-Elipses',
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'WorkingDays',
      text: formatMessage('working_days'),
      classes: 'show-Elipses',
      sort: false,
    },

    {
      dataField: 'ReturnProcessName',
      text: formatMessage('work_process'),
      classes: 'show-Elipses',
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'SubContractorName',
      text: formatMessage('sub_contractor_name'),
      classes: 'show-Elipses',
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'Worker',
      text: formatMessage('worker'),
      classes: 'show-Elipses',
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'Katashiki',
      text: formatMessage('katashiki'),
      classes: 'show-Elipses katashiki-col',
      formatter: (cell) => typeof cell === 'string' && !cell.trim()
        ? '-'
        : <span className="nc-pill">{cell}</span>,
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'Kishu',
      text: formatMessage('kishu'),
      classes: 'show-Elipses',
      formatter: (cell) => typeof cell === 'string' && !cell.trim()
        ? '-'
        : <span className="nc-pill">{cell}</span>,
      title: (cell: string) => {
        return cell?.length > 10 ? cell : undefined;
      },
      sort: true,
    },

    {
      dataField: 'NonConformanceItems',
      text: formatMessage('number_of_defective_items'),
      classes: 'show-Elipses',
      formatter: (cell) => cell?.length ?? 0,
      sort: false,
    },

    {
      dataField: 'CorrectionTime',
      text: formatMessage('correction_time'),
      classes: 'show-Elipses',
      sort: false,
    },

    {
      dataField: '',
      formatter: (cell: any, record: IGetNonConformance) => {
        const isActionable =
          record.Status === NonConformityStatusEnum.Pending ||
          record.Status === NonConformityStatusEnum.Verify;

        if (!isActionable) {
          return record.Status === NonConformityStatusEnum.Complete ? (
            <span className="badge bg-success fw-normal nc-status-complete">
              <FormattedMessage id="is_completed" />
            </span>
           ) : record.Status === NonConformityStatusEnum.NG ? (
            <span className="badge bg-light-danger fw-normal nc-status-ng">
              <FormattedMessage id="in_progress" />
            </span>
          ) : (
            <span className="badge bg-secondary fw-normal nc-status-inprogress">
              <FormattedMessage id="in_progress" />
            </span>
          );
        }

        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {actionIcons
              .filter((action) =>
                record.Status === NonConformityStatusEnum.Pending
                  ? action.action === 'approve'
                  : action.action === 'verify'
              )
              .map((action) => (
                <button
                  key={action.action}
                  type="button"
                  className={`btn btn-${action.varient} btn-sm d-flex gap-2 align-items-center justify-content-center`}
                  title={formatMessage(action.title)}
                  disabled={isOperator && !record.OperatorApproveType}
                  onClick={(evt) => {
                    evt.stopPropagation();
                    action.handler(record);
                  }}
                >
                  <span className="action-icon-wrap">{action.icon}</span>
                  {formatMessage(action.title)}
                </button>
              ))}
          </div>
        );
      },
      text: formatMessage('actions'),
      classes: 'nc-col-center',
      hidden: actionIcons.length > 0 ? false : true,
    },
  ];

  const rowClasses = (record: IGetNonConformance) =>
    `nc-row ${record.Status === 1
      ? 'row-status-approved'
      : record.Status === 2
        ? 'row-status-pending'
        : record.Status === 3
          ? 'row-status-ng-approved'
          : ''
    }`;

  const expandRow = {
    renderer: (record: IGetNonConformance) => {
      const detailAccentClass =
        record.Status === 1
          ? 'nc-detail-approved'
          : record.Status === 2
            ? 'nc-detail-verified'
            : '';

      return (
        <div className={`nc-detail-cell ${detailAccentClass}`}>
          <table className="table table-sm table-bordered mb-0 nc-subtable">
            <thead>
              <tr className="table-secondary">
                <th><FormattedMessage id="subject" /></th>
                <th><FormattedMessage id="item" /></th>
                <th><FormattedMessage id="bom_info" /></th>
                <th className="nc-col-num"><FormattedMessage id="defective_item_count" /></th>
                <th className="nc-defect-details"><FormattedMessage id="defect_details" /></th>
              </tr>
            </thead>
            <tbody>
              {record.NonConformanceItems.map((item) => (
                <tr key={item.Id}>
                  <td>{item.Subject || emptyValue}</td>
                  <td>{item.Item || emptyValue}</td>
                  <td>{item.BOMInfo || emptyValue}</td>
                  <td className="nc-col-num">{item.DefectiveItemCount || emptyValue}</td>
                  <td className="nc-defect-details">{item.DefectDetails || emptyValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
    expanded: allNonConformance.filter((r) => openIds.has(r.Id)).map((r) => r.Id),
    onExpand: (record: IGetNonConformance, isExpanded: boolean) => {
      toggleItems(record.Id);
    },
    onExpandAll: (isExpandAll: boolean, rows: IGetNonConformance[]) => {
      // setOpenIds(isExpandAll ? new Set(rows.map((r) => r.Id)) : new Set());
    },
    expandByColumnOnly: false,
    showExpandColumn: true,
    // expandHeaderColumnRenderer: () => null,
    expandColumnRenderer: ({ expanded }: { expanded: boolean }) => (
      <span className={`nc-toggle-icon${expanded ? ' is-open' : ''}`}>
        {expanded ? '−' : '+'}
      </span>
    ),
  };

  return (
    <>
      {props.showNonConformityPopup && selectedRow && (
        <AddUpdateNonConformity
          id={nonConformity?.Id || selectedRow?.Id}
          modalClosed={props.nonConformityToggleHandler}
          ProductId={nonConformity?.Tray}
          ProductName={nonConformity?.ProductId}
          ProcessId={
            nonConformity?.DetectionProcessId ||
            nonConformity?.ReturnProcessId ||
            0
          }
          TrayId={nonConformity?.TrayId}
          Katashiki={nonConformity?.Katashiki}
          Kishu={nonConformity?.Kishu}
          processName={nonConformity?.DetectionProcessName}
          quantity={nonConformity?.Quantity}
          IsNC={true}
          NCID={nonConformity?.Id}
          status={nonConformity?.Status}
          StartTime={''}
          Duration={0}
        />
      )}
      <div className="nc-wrap">
        <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
          <div className="row align-items-center order-table">
            <Header
              className="col-auto"
              label="Nonconformity"
            />
            <div className="col">
              <ul className="add-list">
                <li title="Refresh">
                  <a
                    className="btn btn-light"
                    onClick={handleRefresh}
                  >
                    <RefreshIcon />
                  </a>
                </li>

                <ul className="tableAccordian-switch d-flex">
                  <li
                    className={viewMode === 'normal' ? 'active' : ''}
                    onClick={() => handleViewModeChange('normal')}
                  >
                    <OrderTableDefaultViewIcon />
                    <span className="icon-text">
                      <FormattedMessage id="default_view" />
                    </span>
                  </li>
                  <li
                    className={viewMode === 'assigned' ? 'active' : ''}
                    onClick={() => handleViewModeChange('assigned')}
                  >
                    <OrderTableShowAssignedIcon />
                    <span className="icon-text">
                      <FormattedMessage id="waiting_for_approval" />
                    </span>
                  </li>
                  <li
                    className={viewMode === 'completed' ? 'active' : ''}
                    onClick={() => handleViewModeChange('completed')}
                  >
                    <OrderTableShowCompletedIcon />
                    <span className="icon-text">
                      <FormattedMessage id="waiting_for_verification" />
                    </span>
                  </li>
                </ul>
              </ul>
            </div>
          </div>
        </div>

        <FormLoader loading={getAllNonConformanceState.inProgress} />

        <div className="card card-page">
          <div>
            <div className="tableDesign row-eq-height tableDevices p-3 changingPanel">
              {!getAllNonConformanceState.inProgress &&
                !allNonConformance.length ? (
                <div className="message">
                  <div className="d-flex message-area">
                    <div className="no-data-img">
                      <img
                        src={noMessageIcon}
                        alt=""
                        className="no_message"
                      />
                    </div>
                    <div>
                      <h3 className="fw-bold display-6">
                        {' '}
                        <FormattedMessage id="no_nonconformity" />
                      </h3>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="table-responsive nc-table-wrap"
                  data-testid="table"
                >
                  <div
                    ref={scrollSentinelRef}
                    className="nc-scroll-sentinel"
                    aria-hidden="true"
                  />
                  <Grid
                    key={preferencesStore.getLanguage}
                    keyField="Id"
                    data={allNonConformance}
                    columns={columns}
                    rowClasses={rowClasses}
                    expandRow={expandRow}
                    loading={getAllNonConformanceState.inProgress}
                    sort={{ ...columSortOrder }}
                    remote={{ ...GridRemoteConstant }}
                    onTableChange={handleTableChange}
                    classes="nc-table"
                    headerWrapperClasses={`nc-thead${isTableScrolled ? ' nc-thead-scrolled' : ''}`}
                  />
                </div>
              )}
            </div>
          </div>

          {allNonConformance.length && (
            <div className="row no-gutters p-3 pt-0 tableFooter">
              <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
                <FormattedMessage id="show" />
                <select
                  data-testid="pageSelect"
                  className="pageLimit"
                  onChange={onPageLimitChanged}
                  value={pageLimitInfo.pageLimit}
                >
                  {pageLimitOptions.map((op: any) => (
                    <option
                      key={op}
                      value={op}
                    >
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
                <Pagination
                  isRefreshPage={false}
                  totalPages={
                    NonConformanceResponseList.PagingDetails.TotalPages
                  }
                  pageLimit={pageLimitInfo.pageLimit}
                  onPageChanged={onPageChanged}
                  disablePagination={disablePagination}
                  setIsRefreshPage={setIsRefreshPage}
                  currentPage={
                    NonConformanceResponseList.PagingDetails.CurrentPage
                  }
                  totalRecords={
                    NonConformanceResponseList.PagingDetails.TotalRecords
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NonConformityPopupHOC(observer(Nonconformity));
